import { FC } from 'react'

import { match } from 'ts-pattern'

import {
  Text,
  EmailPreview,
  IconEmail,
  GreyGrey80,
  Primary13Regular,
  IconXMark
} from '@sequencehq/core-components'

import { getLogoSrc } from 'Customer/index'
import { NodeTimeline } from 'Automations/components'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { EmailTrigger, NotificationTrigger, TaskInput } from './types'
import { useCustomerData } from '../../hooks/useCustomerData'

type Customer = Dashboardv99990101CommonModels.Customers.Customer

export const GenericNotificationPreview = (props: { taskId: string }) => {
  const { manualTask } = useManualTask({ taskId: props.taskId })
  const input = manualTask?.input as TaskInput | undefined
  const { customer, isPending: isCustomerPending } = useCustomerData(
    input?.customerId
  )

  // Gate on the customer query the same way the list item does, so the trigger
  // card doesn't first render with the raw email address/no logo and then flip
  // to the customer name + logo once the query resolves.
  if (!input || isCustomerPending) {
    return null
  }

  return (
    <>
      {input.trigger && (
        <TriggerTimelineItem trigger={input.trigger} customer={customer} />
      )}

      <NodeTimeline.Item icon={<IconXMark />} showStem={false}>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label={input.title}
            timestamp={manualTask?.createdAt}
          />
        </NodeTimeline.Title>

        <Text {...Primary13Regular} color={GreyGrey80}>
          {input.body}
        </Text>
      </NodeTimeline.Item>
    </>
  )
}

const TriggerTimelineItem: FC<{
  trigger: NotificationTrigger
  customer?: Customer
}> = ({ trigger, customer }) =>
  match(trigger)
    .with({ type: 'EMAIL' }, emailTrigger => (
      <NodeTimeline.Item icon={<IconEmail />} showStem>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label="Email forwarded"
            timestamp={emailTrigger.receivedAt}
          />
        </NodeTimeline.Title>

        <EmailTriggerCard trigger={emailTrigger} customer={customer} />
      </NodeTimeline.Item>
    ))
    // Trigger kinds are designed to stay additive (see types.ts) and the backend
    // controls `trigger.type`, so an unrecognised type is reachable. Render
    // nothing rather than throwing, which the ErrorBoundary would otherwise
    // escalate to a full-page error that also hides the dismiss action.
    .otherwise(() => null)

const EmailTriggerCard: FC<{
  trigger: EmailTrigger
  customer?: Customer
}> = ({ trigger, customer }) => (
  <EmailPreview
    data-testid="watchtower.notification.genericNotification.emailTrigger"
    // Starts as a compact "logo + subject" row; the operator can expand it to
    // read the forwarded email's sender and body when deciding how to action it.
    startCollapsed
    width="100%"
    from={{
      name: customer?.legalName ?? trigger.from,
      email: trigger.from,
      logoUrl: customer ? getLogoSrc(customer) : undefined
    }}
    // A forwarded email carries no recipient list into the notification.
    to={[]}
    subject={trigger.subject}
    // The excerpt is plain text (e.g. a forwarded message with header lines);
    // preserve its line breaks rather than collapsing them as HTML would.
    body={trigger.excerpt}
    plainTextBody
  />
)
